import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { cn } from '../utils/cn.js';
const ToastContext = createContext(null);
const VARIANT_ICONS = {
    info: _jsx(Info, { size: 18, strokeWidth: 1.5 }),
    success: _jsx(CheckCircle2, { size: 18, strokeWidth: 1.5 }),
    warning: _jsx(AlertTriangle, { size: 18, strokeWidth: 1.5 }),
    error: _jsx(XCircle, { size: 18, strokeWidth: 1.5 }),
};
export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const nextId = useRef(1);
    const dismiss = useCallback((id) => {
        setToasts((current) => current.filter((t) => t.id !== id));
    }, []);
    const toast = useCallback((input) => {
        const id = nextId.current++;
        const record = { ...input, id };
        setToasts((current) => [...current, record]);
        const duration = input.duration ?? 5000;
        if (duration > 0) {
            setTimeout(() => dismiss(id), duration);
        }
        return id;
    }, [dismiss]);
    const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);
    return (_jsxs(ToastContext.Provider, { value: value, children: [children, _jsx(ToastRegion, { toasts: toasts, onDismiss: dismiss })] }));
}
export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) {
        throw new Error('useToast must be used inside <ToastProvider>');
    }
    return ctx;
}
function ToastRegion({ toasts, onDismiss }) {
    return (_jsx("div", { className: "clari-toast-region", role: "region", "aria-label": "Notifications", children: toasts.map((t) => (_jsx(ToastView, { record: t, onDismiss: onDismiss }, t.id))) }));
}
function ToastView({ record, onDismiss }) {
    const variant = record.variant ?? 'info';
    // Re-trigger animation if mounting in StrictMode dev.
    useEffect(() => {
        /* noop — placeholder for future analytics hook */
    }, [record.id]);
    return (_jsxs("div", { role: variant === 'error' || variant === 'warning' ? 'alert' : 'status', className: cn('clari-toast', `clari-toast--${variant}`), children: [_jsx("span", { className: "clari-toast__icon", "aria-hidden": "true", children: VARIANT_ICONS[variant] }), _jsxs("div", { className: "clari-toast__body", children: [_jsx("div", { className: "clari-toast__title", children: record.title }), record.description ? (_jsx("div", { className: "clari-toast__description", children: record.description })) : null] }), _jsx("button", { type: "button", className: "clari-toast__close", onClick: () => onDismiss(record.id), "aria-label": record.closeLabel, children: _jsx(X, { size: 16, strokeWidth: 1.5 }) })] }));
}
//# sourceMappingURL=Toast.js.map